/* eslint-disable react/no-multi-comp */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Fab, Grid, useTheme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import * as R from 'ramda'
import {
  type ClassesType,
  Defaults,
  muiTheme,
  PermissionArea,
  type PrimitiveTableColumn,
  type TableFilter,
  Text,
} from '@pbt/pbt-ui-components'

import TeamMemberFilter from '~/components/common/filters/TeamMemberFilter'
import ExpandableTable from '~/components/common/lists/ExpandableTable'
import PrimitiveTableWithSearchHighlights from '~/components/common/lists/primitive-table/PrimitiveTableWithSearchHighlights'
import ConversationPatientFilter from '~/components/dashboard/communications/table/ConversationPatientFilter'
import { ConversationCategory } from '~/constants/communications'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { BaseRoute } from '~/constants/routes'
import i18n from '~/locales/i18n'
import { useConversationList } from '~/store/hooks/conversations'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getConversationListFetching,
  getConversationsListObjects,
  getConversationsTotalCount,
} from '~/store/reducers/conversations'
import type { Conversation } from '~/types'
import { getUrlSearchParam } from '~/utils'
import {
  getConversationRowColoring,
  getConversationStatus,
  hasAppliedFilters,
  mapDateToConversationDetailsDate,
} from '~/utils/communicationsUtils'
import { usePersistClickedItemOnURL } from '~/utils/routes'
import useDialog from '~/utils/useDialog'

import ConversationStatusLabel from '../common/ConversationStatusLabel'
import { CreatedConversationFlow } from '../common/CreatedConversationsInfoDialog'
import ConversationFilters from '../filters/ConversationFilters'
import TransportIcon from '../TransportIcon'
import ConversationAssignedCell from './ConversationAssignedCell'
import ConversationDateRangeFilter from './ConversationDateRangeFilter'
import ConversationStatusFilter from './ConversationStatusFilter'
import ConversationSubjectCell from './ConversationSubjectCell'
import ConversationTypeFilter from './ConversationTypeFilter'
import NoConversationsScreen from './NoConversationsScreen'

const useStyles = makeStyles(
  (theme) => ({
    headerButtons: {
      padding: theme.spacing(1, 3),
    },
    newConversationButton: {
      height: 40,
      flexShrink: 0,
    },
    pageTitle: {
      lineHeight: '2.4rem',
    },
  }),
  { name: 'ConversationsTableComponent' },
)

const columns: PrimitiveTableColumn[] = [
  {
    label: i18n.t('Common:CONTACT_ONE'),
    prop: 'contactName',
    width: 2,
    color: (item: Conversation) =>
      item.isSystemMessage ? muiTheme.colors.grayGray2 : 'inherit',
  },
  {
    label: i18n.t('Common:PATIENT'),
    prop: (item) =>
      item.patientName
        ? item.patientName
        : i18n.t('Communications:PATIENTS.NOT_ASSOCIATED'),
    filter: 'patientIds',
    FilterComponent: ConversationPatientFilter,
    width: 2,
    color: (item: Conversation) =>
      item.isSystemMessage ? muiTheme.colors.grayGray2 : 'inherit',
  },
  {
    label: i18n.t('Common:SUBJECT'),
    component: ConversationSubjectCell,
    width: 4,
  },
  {
    label: i18n.t('Common:ASSIGNEE'),
    component: ConversationAssignedCell,
    filter: 'assignee',
    FilterComponent: TeamMemberFilter,
    width: 3,
  },
  {
    label: i18n.t('Common:STATUS'),
    component: (item: Conversation) => (
      <ConversationStatusLabel
        conversationId={item.id}
        status={getConversationStatus(item)}
      />
    ),
    filter: 'status',
    FilterComponent: ConversationStatusFilter,
    width: 1.5,
  },
  {
    label: i18n.t('Common:LAST_MESSAGE'),
    prop: 'lastMessageDate',
    filter: 'lastMessageDate',
    FilterComponent: ConversationDateRangeFilter,
    width: 2,
    color: (item: Conversation) =>
      item.isSystemMessage ? muiTheme.colors.grayGray2 : 'inherit',
  },
  {
    label: i18n.t('Common:TYPE_ONE'),
    component: (item: Conversation) => <TransportIcon type={item.transport} />,
    filter: 'type',
    FilterComponent: ConversationTypeFilter,
    width: 1.5,
  },
]

interface HeaderButtonsProps {
  classes?: ClassesType<typeof useStyles>
  onNewConversationClick: () => void
  showCreate?: boolean
}

const HeaderButtons = ({
  showCreate = true,
  onNewConversationClick,
  classes: classesProp,
}: HeaderButtonsProps) => {
  const classes = useStyles({ classes: classesProp })
  const permissions = useSelector(getCRUDByArea(PermissionArea.COMMUNICATION))
  const { t } = useTranslation(['Common'])

  return (
    <Grid container className={classes.headerButtons} wrap="nowrap">
      <Text className={classes.pageTitle} mr={3} variant="h1">
        {t('Common:COMMUNICATION')}
      </Text>
      <Grid container item justifyContent="space-between">
        <ConversationFilters />
        {permissions.create && showCreate && (
          <Fab
            className={classes.newConversationButton}
            color="inherit"
            variant="extended"
            onClick={onNewConversationClick}
          >
            {t('Common:NEW_CLIENT_CONVERSATION')}
          </Fab>
        )}
      </Grid>
    </Grid>
  )
}

interface ConversationsTableComponentProps {
  classes?: ClassesType<typeof useStyles>
  clientId?: string
  showNewConversation?: boolean
}

const ConversationsTableComponent = ({
  clientId,
  classes: classesProp,
  showNewConversation,
}: ConversationsTableComponentProps) => {
  const theme = useTheme()
  const classes = useStyles({ classes: classesProp })
  const navigate = useNavigate()
  const location = useLocation()

  const [openNewConversationDialog] = useDialog(
    DialogNames.NEW_CONVERSATION_WITH_STEPS,
  )
  const [openCreatedConversationsInfoDialog] = useDialog(
    DialogNames.CREATED_CONVERSATIONS_INFO_DIALOG,
  )

  const conversations = useSelector(getConversationsListObjects)
  const conversationsTotalCount = useSelector(getConversationsTotalCount)
  const isLoading = useSelector(getConversationListFetching)
  const permissions = useSelector(getCRUDByArea(PermissionArea.COMMUNICATION))
  const isCVCSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )

  const getActualRowColoring = R.curry(getConversationRowColoring)(theme)

  const [scrollToItem, setScrollToItem] = useState(0)
  const [hasFreshData, setHasFreshData] = useState(false)
  const persistClickedItem = usePersistClickedItemOnURL()

  const initialItemParam =
    Number(getUrlSearchParam('initialItem', location.search)) || 0
  // If contextual back button is enabled:
  //   - If initial item is near the start (< 10), load from beginning (0)
  //   - Otherwise, load one batch size before the initial item
  // If contextual back button disabled, always start from beginning (0)
  // This is because loadUnloadedRanges on InfiniteLoaderList is finicky when going backwards
  const queryFrom =
    Math.max(initialItemParam - Defaults.CONVERSATIONS_BATCH_LOAD_COUNT, 0) < 10 // This is really "offset" because GQL endpoint
      ? 0
      : Math.max(initialItemParam - Defaults.CONVERSATIONS_BATCH_LOAD_COUNT, 0)
  const queryTo = initialItemParam // This is really "limit" because GQL endpoint
    ? Defaults.CONVERSATIONS_BATCH_LOAD_COUNT * 2
    : Defaults.CONVERSATIONS_BATCH_LOAD_COUNT

  const tableColumns = isCVCSystemMessageSuppressionEnabled
    ? columns
    : columns.filter((column) => column.filter !== 'patientIds')

  const { loadMoreItems, clearFilters, applyFilter, filters } =
    useConversationList({
      clientId,
      queryFrom,
      queryTo,
      fillPrecedingWithNull: queryFrom > 0,
    })

  // eslint-disable-next-line react/hook-use-state
  const [lastFilters] = useState(filters)

  const areFiltersEqual = (
    a: Record<string, TableFilter>,
    b: Record<string, TableFilter>,
  ) => Object.keys(a).every((key) => a[key]?.value === b[key]?.value)

  // When filters change, reset initialItemParam, queryTo and queryFrom, and URL on navigation
  useEffect(() => {
    if (!areFiltersEqual(filters, lastFilters) && scrollToItem) {
      navigate(window.location.href, { replace: true })
    }
  }, [filters])

  // Set hasFreshData to true after first successful load
  useEffect(() => {
    if (!isLoading && conversations.length > 0 && !hasFreshData) {
      setHasFreshData(true)
    }
  }, [isLoading, conversations.length])

  // Scroll to index after everything loaded with fresh data
  useEffect(() => {
    if (
      hasFreshData &&
      !isLoading &&
      conversations.length > 0 &&
      scrollToItem !== initialItemParam
    ) {
      setScrollToItem(initialItemParam)
      // Remove initialItem param from URL so it isn't used on page reload
      const url = new URL(window.location.href)
      url.searchParams.delete('initialItem')
      window.history.replaceState(
        { ...window.history.state, as: url.href, url: url.href },
        '',
        url.href,
      )
    }
  }, [
    isLoading,
    conversations.length,
    initialItemParam,
    scrollToItem,
    hasFreshData,
  ])

  const filtersWithoutCategory = R.omit(['category'], filters) as Record<
    string,
    TableFilter[]
  >

  const hasSelectedFilters = hasAppliedFilters(filtersWithoutCategory)
  const mapConversationDate = (conversation: Conversation) =>
    mapDateToConversationDetailsDate(conversation.lastMessageDate || '')

  const tableConversations = conversations.map((conversation) =>
    conversation
      ? {
          ...conversation,
          contactName: conversation.to,
          lastMessageDate: mapConversationDate(conversation),
          subject: conversation.title,
          isSystemMessage:
            conversation.isSystemMessage &&
            isCVCSystemMessageSuppressionEnabled,
        }
      : undefined,
  )

  const navigateToConversation = (id: string) => {
    const index = tableConversations.findIndex((conv) => conv?.id === id)
    if (index >= 0) {
      // -1 would mean a new item just added, so we don't to persist the index
      persistClickedItem(index)
    }
    navigate(`${BaseRoute.COMMUNICATIONS}/${id}`, { state: { clientId } })
  }

  const handleConversationCreated = (flow: CreatedConversationFlow) => {
    if (flow === CreatedConversationFlow.DRAFT_MESSAGE_SENT) {
      applyFilter('category', { value: ConversationCategory.DRAFT })
    }

    openCreatedConversationsInfoDialog({
      flow,
      handleViewConversation: navigateToConversation,
    })
    navigate(window.location.href, { replace: true })
  }

  const handleNewConversationClick = () =>
    openNewConversationDialog({ clientId, handleConversationCreated })
  const isItemLoaded = (index: number) => Boolean(tableConversations[index])

  useEffect(() => {
    if (showNewConversation) {
      handleNewConversationClick()
    }
  }, [showNewConversation])

  return (
    <ExpandableTable
      NoItemsScreen={NoConversationsScreen}
      NoItemsScreenProps={{
        Header: (
          <HeaderButtons
            classes={classes}
            showCreate={false}
            onNewConversationClick={handleNewConversationClick}
          />
        ),
        categoryFilter: filters?.category?.value,
        onSentClick: () =>
          applyFilter('category', { value: ConversationCategory.SENT }),
        onNewConversationClick: permissions.create
          ? handleNewConversationClick
          : undefined,
      }}
      hasSelectedFilters={hasSelectedFilters}
      headerButtons={
        <HeaderButtons
          classes={classes}
          onNewConversationClick={handleNewConversationClick}
        />
      }
      isLoading={isLoading}
      list={tableConversations}
      onSelected={navigateToConversation}
    >
      <PrimitiveTableWithSearchHighlights
        columns={tableColumns}
        disableInitialAutoScroll={Boolean(initialItemParam)}
        filters={filters}
        focusedListItem={scrollToItem}
        getRowColoring={getActualRowColoring}
        isItemLoaded={isItemLoaded}
        list={tableConversations}
        loadMoreItems={loadMoreItems}
        totalCount={conversationsTotalCount}
        onApplyFilter={applyFilter}
        onClearFilters={clearFilters}
      />
    </ExpandableTable>
  )
}

export default ConversationsTableComponent
